import * as moment from "moment";
import { Injectable } from "@angular/core";
import { BaseComponent } from "src/app/components/base.component";
import { AutoDrawModels } from "src/app/core/models";
import { TranslateService } from '@ngx-translate/core';
import { AUTO_DRAW_ENUM } from 'src/app/core/enum/index';
import { AlertService } from "src/app/shared/alert.service";


 /**
  * 問卷系統通用function
  */
@Injectable({
    providedIn: 'root'
})
export class CheckForm extends BaseComponent {

    public LENGTH_WIDTH_LIMIT = AUTO_DRAW_ENUM.LENGTH_WIDTH_LIMIT;
	public THICKNESS_LIMIT = AUTO_DRAW_ENUM.THICKNESS_LIMIT;
	public CHECK_NULL_LIST = AUTO_DRAW_ENUM.CHECK_NULL_LIST;
	public CHECK_INT_LIST = AUTO_DRAW_ENUM.CHECK_INT_LIST;
	public CHECK_POSITIVE_LIST = AUTO_DRAW_ENUM.CHECK_POSITIVE_LIST;
	public MOQ = AUTO_DRAW_ENUM.MOQ;
	public TIM_TYPE = AUTO_DRAW_ENUM.TIM_TYPE;

    constructor(
        public translate: TranslateService,
		private alertServ: AlertService
    ) {
        super()
    }

	/**
	 * 檢查 tim 備註是否超過輸入長度限制
	 * @param tim `AutoDrawModels.Tim`: 受檢查的 tim 實例
	 * @param max `number`: 長度上限
	 * @returns `boolean`: `true` 超過, `false` 沒超過
	 */
	public checkMemoLength(tim: AutoDrawModels.Tim, max: number): boolean {
		const isOver = tim.memo ? tim.memo.trim().length > max : false;
		return isOver;
	}

	/**
	 * 檢查tim需求總量MOQ限制，回傳是否超出限制
	 * @param tim `AutoDraw.Tim`: tim 實例
	 * @returns `boolean`
	 */
	public checkMoq(tim: AutoDrawModels.Tim): boolean{
		// 導熱硅脂才檢查MOQ
		if (tim.tim_type !== this.TIM_TYPE['TMP']) return false;
		const isOver = <number>tim.order_quantity < this.MOQ.TMP;
		if (isOver) {
			let targetMsg = this.translate.instant("AUTO_DRAW.ENUM.TIM_KEY.NUMBER") + `：${tim.number}<br>`;
			let msg = this.translate.instant("AUTO_DRAW.ALERT.MOQ", {min: this.MOQ.TMP});
			this.alertServ.openAlert(targetMsg + msg);
		}
		return isOver
	}


	/**
	 * 檢查tim厚度限制，回傳是否超出限制
	 * @param tim `AutoDraw.Tim`: tim 實例
	 * @returns `boolean`
	 */
	public checkThickness(tim: AutoDrawModels.Tim): boolean{
		const totalThickness = <number>tim.thickness;
		const timWmk = <number>tim.thermal_coefficient;
		let minLimit = timWmk < this.THICKNESS_LIMIT.WMK_BOUNDARY ? 
			this.THICKNESS_LIMIT.LOW_WMK_MIN : this.THICKNESS_LIMIT.HEIGHT_WMK_MIN;
		let maxLimit = timWmk < this.THICKNESS_LIMIT.WMK_BOUNDARY ? 
			this.THICKNESS_LIMIT.LOW_WMK_MAX : this.THICKNESS_LIMIT.HEIGHT_WMK_MAX;
		
		const isOver = totalThickness < minLimit || maxLimit < totalThickness;
		if (isOver) {
			let targetMsg = this.translate.instant("AUTO_DRAW.ENUM.TIM_KEY.NUMBER") + `：${tim.number}<br>`;
			let msg = this.translate.instant("AUTO_DRAW.ALERT.THICKNESS", {
				min: minLimit,
				max: maxLimit
			});
			this.alertServ.openAlert(targetMsg + msg);
		}
		return isOver
	}

	/**
	 * 檢查tim長寬限制，回傳是否超出限制
	 * @param tim `AutoDraw.Tim`: tim 實例
	 * @returns `boolean`
	 */
	public checkLengthWidth(tim: AutoDrawModels.Tim): boolean{
		const flatLength = this.LENGTH_WIDTH_LIMIT.FLAT_LENGTH;
		const flatWidth = this.LENGTH_WIDTH_LIMIT.FLAT_WIDTH;
		
		let minLengthLimit = 0
		let minWidthLimit = 0
		minLengthLimit = tim.pulltab ? this.LENGTH_WIDTH_LIMIT.LENGTH_HAS_PULLTAB : this.LENGTH_WIDTH_LIMIT.LENGTH_NO_PULLTAB;
		minWidthLimit = tim.pulltab ? this.LENGTH_WIDTH_LIMIT.WIDTH_HAS_PULLTAB : this.LENGTH_WIDTH_LIMIT.WIDTH_NO_PULLTAB;
		const lengthIsBigger = <number>tim.length > <number>flatLength;
		const widthIsBigger = <number>tim.width > <number>flatWidth;
		const lengthIsLower = <number>tim.length < minLengthLimit;
		const widthIsLower = <number>tim.width < minWidthLimit;
		const isOver = lengthIsBigger || widthIsBigger || lengthIsLower || widthIsLower;
		if(isOver){
			let targetMsg = this.translate.instant("AUTO_DRAW.ENUM.TIM_KEY.NUMBER") + `：${tim.number}<br>`;
			let msg = this.translate.instant("AUTO_DRAW.ALERT.SPEC", {
				minLength: minLengthLimit,
				minWidth: minWidthLimit,
				maxLength: <number>flatLength,
				maxWidth: <number>flatWidth
			});
			this.alertServ.openAlert(targetMsg + msg);
		}

		return isOver
	}

	/**
	 * 檢查整數項目
	 * @returns `boolean`
	 */
	public checkInt(tim: AutoDrawModels.Tim): boolean{
        let invalidKey = '';
		let checkList = tim.tim_type === this.TIM_TYPE["TMP"] ? this.CHECK_INT_LIST.TMP : this.CHECK_INT_LIST.DEFAULT;
		const hasNull = checkList.some((key: keyof AutoDrawModels.Tim) => {
            const isNotInt = !Number.isInteger(tim[key]) && tim[key] !== 0;
            if (isNotInt) invalidKey = key.toString();
            return isNotInt;
        });
		if(hasNull) {
			let targetMsg = this.translate.instant("AUTO_DRAW.ENUM.TIM_KEY.NUMBER") + `：${tim.number}<br>`;
			let msg = this.translate.instant("AUTO_DRAW.ALERT.INT", {
                key: this.translate.instant(`AUTO_DRAW.ENUM.TIM_KEY.${invalidKey.toUpperCase()}`)
            });
			this.alertServ.openAlert(targetMsg + msg);
		}

		return hasNull
	}

	/**
	 * 檢查是否有負數
	 * @returns `boolean`
	 */
	public checkPositive(tim: AutoDrawModels.Tim): boolean{
        let invalidKey = ''
		const hasNegative = this.CHECK_POSITIVE_LIST.some((key: keyof AutoDrawModels.Tim) => {
            const isNegative = tim[key] < 0;
            if (isNegative) invalidKey = key.toString();
            return isNegative;
        });
		if(hasNegative) {
			let targetMsg = this.translate.instant("AUTO_DRAW.ENUM.TIM_KEY.NUMBER") + `：${tim.number}<br>`;
			let msg = this.translate.instant("AUTO_DRAW.ALERT.POSITIVE", {
                key: this.translate.instant(`AUTO_DRAW.ENUM.TIM_KEY.${invalidKey.toUpperCase()}`)
            });
			this.alertServ.openAlert(targetMsg + msg);
		}

		return hasNegative
	}
	
	/**
	 * 檢查必填項目，回傳是否有空值
	 * @returns `boolean`
	 */
	public checkNull(tim: AutoDrawModels.Tim): boolean{
		let checkList = tim.tim_type === this.TIM_TYPE['TMP'] ? this.CHECK_NULL_LIST.TMP : this.CHECK_NULL_LIST.DEFAULT;
		const hasNull = checkList.some((key: keyof AutoDrawModels.Tim) => !tim[key]);
		if(hasNull) {
			let targetMsg = this.translate.instant("AUTO_DRAW.ENUM.TIM_KEY.NUMBER") + `：${tim.number}<br>`;
			let msg = this.translate.instant("AUTO_DRAW.ALERT.NULL");
			this.alertServ.openAlert(targetMsg + msg);
		}

		return hasNull
	}

}